/**
 * LanguageTabs Component - Multi-language tab navigation for coupon content
 *
 * Story 4.2: Coupon Content & Multi-Language Support
 * AC #1, #2, #3: Language tabs with add/remove functionality
 */

import React from 'react';
import { useTranslation } from '@/i18n';
import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SupportedLanguage, LanguageTabsProps } from '@/types/ui.types';

const AVAILABLE_TRANSLATIONS: Array<{ code: 'fr' | 'zh'; label: string }> = [
	{ code: 'fr', label: 'French' },
	{ code: 'zh', label: 'Chinese' },
];

const MAX_LANGUAGES = 3;

export const LanguageTabs: React.FC<LanguageTabsProps> = ({
	enabledLanguages,
	activeLanguage,
	onLanguageSelect,
	onLanguageAdd,
	onLanguageRemove,
	disabled = false,
}) => {
	const { t } = useTranslation();

	// Languages that can still be added
	const availableToAdd = AVAILABLE_TRANSLATIONS.filter(
		(lang) => !enabledLanguages.includes(lang.code)
	);

	const canAddMore = enabledLanguages.length < MAX_LANGUAGES;

	const getLanguageLabel = (code: SupportedLanguage): string => {
		switch (code) {
			case 'en':
				return t('merchant.coupon.form.languageTabEnglish');
			case 'fr':
				return t('merchant.coupon.form.languageTabFrench');
			case 'zh':
				return t('merchant.coupon.form.languageTabChinese');
			default:
				return code;
		}
	};

	return (
		<div className="flex flex-wrap items-center gap-2 mb-4 border-b border-gray-200 pb-3">
			{/* English tab - always visible, not removable */}
			<button
				type="button"
				onClick={() => onLanguageSelect('en')}
				disabled={disabled}
				className={cn(
					'px-4 py-2 rounded-t-lg font-medium text-sm transition-colors',
					activeLanguage === 'en'
						? 'bg-blue-600 text-white'
						: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
					disabled && 'opacity-50 cursor-not-allowed'
				)}
			>
				{getLanguageLabel('en')}
			</button>

			{/* French tab - if enabled */}
			{enabledLanguages.includes('fr') && (
				<div className="relative flex items-center">
					<button
						type="button"
						onClick={() => onLanguageSelect('fr')}
						disabled={disabled}
						className={cn(
							'px-4 py-2 rounded-t-lg font-medium text-sm transition-colors pr-8',
							activeLanguage === 'fr'
								? 'bg-blue-600 text-white'
								: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
							disabled && 'opacity-50 cursor-not-allowed'
						)}
					>
						{getLanguageLabel('fr')}
					</button>
					{!disabled && (
						<button
							type="button"
							onClick={() => onLanguageRemove('fr')}
							className={cn(
								'absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-red-100 transition-colors',
								activeLanguage === 'fr' ? 'text-white hover:text-red-600' : 'text-gray-500 hover:text-red-600'
							)}
							title={t('merchant.coupon.form.removeTranslation')}
						>
							<X className="w-3 h-3" />
						</button>
					)}
				</div>
			)}

			{/* Chinese tab - if enabled */}
			{enabledLanguages.includes('zh') && (
				<div className="relative flex items-center">
					<button
						type="button"
						onClick={() => onLanguageSelect('zh')}
						disabled={disabled}
						className={cn(
							'px-4 py-2 rounded-t-lg font-medium text-sm transition-colors pr-8',
							activeLanguage === 'zh'
								? 'bg-blue-600 text-white'
								: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
							disabled && 'opacity-50 cursor-not-allowed'
						)}
					>
						{getLanguageLabel('zh')}
					</button>
					{!disabled && (
						<button
							type="button"
							onClick={() => onLanguageRemove('zh')}
							className={cn(
								'absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-red-100 transition-colors',
								activeLanguage === 'zh' ? 'text-white hover:text-red-600' : 'text-gray-500 hover:text-red-600'
							)}
							title={t('merchant.coupon.form.removeTranslation')}
						>
							<X className="w-3 h-3" />
						</button>
					)}
				</div>
			)}

			{/* Add Translation dropdown - shown when can add more */}
			{canAddMore && availableToAdd.length > 0 && !disabled && (
				<div className="relative group">
					<button
						type="button"
						className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
					>
						<Plus className="w-4 h-4 mr-1" />
						{t('merchant.coupon.form.addTranslation')}
					</button>
					<div className="absolute left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
						{availableToAdd.map((lang) => (
							<button
								key={lang.code}
								type="button"
								onClick={() => onLanguageAdd(lang.code)}
								className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
							>
								{getLanguageLabel(lang.code)}
							</button>
						))}
					</div>
				</div>
			)}

			{/* Max languages reached message */}
			{!canAddMore && !disabled && (
				<span className="text-xs text-gray-500 ml-2">
					{t('merchant.coupon.form.maxLanguagesReached')}
				</span>
			)}
		</div>
	);
};
